import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { FaFire } from 'react-icons/fa';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useServiceActions from '../../../hooks/APIHooks/appointments/useServiceActions';
import useReferralActions from '../../../hooks/APIHooks/users/useReferralActions';
import { createEditReferralSchema } from '../../../schemas';
import { AppointmentRequest } from '../../../types/appointmentRequests';
import { SelectData } from '../../../types/common';
import { ReferralReqBody, ReferredTo } from '../../../types/referral';
import { Service } from '../../../types/services';
import { showFullName } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ReferralModalTable from '../../AppointmentsComps/ListComps/ReferralModalTable';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditReferralModal = ({
  title,
  appointment,
  handleModalClose,
}: {
  title: 'create' | 'update';
  appointment: AppointmentRequest;
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [serviceList, setServiceList] = useState<Array<Service>>([]);
  const [associateList, setAssociateList] = useState<Array<SelectData>>([]);
  const [referredToList, setReferredToList] = useState<Array<ReferredTo>>([]);
  const [selectedService, setSelectedService] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  const [resetField, setResetField] = useState<boolean>(false);

  // integration of context API hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { createNewReferralHandler } = useReferralActions('');
  const { getServiceListHandler, getServiceWiseAssociatesHandler } =
    useServiceActions(
      'service&associate',
      selectedService.id !== -1 ? selectedService.id : undefined
    );

  // setting the service list into a state here
  useEffect(() => {
    if (getServiceListHandler.data?.is_success) {
      setServiceList(getServiceListHandler.data.data);
    }
  }, [getServiceListHandler.data]);

  // setting the associate list into a state here
  useEffect(() => {
    if (getServiceWiseAssociatesHandler.data?.is_success) {
      setAssociateList(
        getServiceWiseAssociatesHandler.data.data
          .filter((associate) => associate.id !== currentUser?.id)
          .filter(
            (associate) =>
              !referredToList.some(
                (referral) =>
                  referral.associate === associate.id &&
                  referral.service === selectedService.id
              )
          )
          .map((associate) => ({
            id: associate.id,
            name: showFullName(
              associate.first_name,
              associate.middle_name,
              associate.last_name
            ),
          }))
      );
    }
  }, [
    getServiceWiseAssociatesHandler.data,
    currentUser,
    referredToList,
    selectedService,
  ]);

  // finding existing service from service list here
  useEffect(() => {
    if (serviceList.length) {
      const existingService = serviceList.find(
        (service) => service.id === appointment.service_id
      );

      if (existingService?.id) {
        setSelectedService({
          id: existingService.id,
          name: existingService.name,
        });
      }
    }
  }, [serviceList, appointment.service_id]);

  // resetting the boolean state value to false after the field is changed here
  useEffect(() => {
    if (resetField) {
      setResetField(false);
    }
  }, [resetField]);

  const createEditReferralHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      const body: ReferralReqBody = {
        appointment_request: appointment.id,
        service: values.service.id,
        referrer: currentUser?.id as number,
        referred_client: appointment.client,
        referred_to: referredToList.map((referral) => ({
          service: referral.service,
          associate: referral.associate,
        })),
        reason: values.reason,
        created_by: currentUser?.username,
      };

      console.log('REFERRAL REQ BODY: ', body, referredToList);

      await createNewReferralHandler
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Referral Created!!');

            handleModalClose();
          } else {
            toast.error(res.message || 'Referral Creation Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // function to add referrals referred to list declared here
  const addReferralToList = (associate: SelectData) => {
    setReferredToList((state) => [
      ...state,
      {
        service: selectedService.id,
        associate: associate.id,
        associate_name: associate.name,
        service_name: selectedService.name,
      },
    ]);

    const filteredAssociates = associateList.filter(
      (act) => act.id !== associate.id
    );

    setAssociateList(filteredAssociates);
  };

  // function to remove referral referred to list declared here
  const removeReferralFromList = (associateId: number, serviceId: number) => {
    const targetedReferral = referredToList.find(
      (referral) =>
        referral.associate === associateId && referral.service === serviceId
    );

    if (targetedReferral?.associate && selectedService.id === serviceId) {
      setAssociateList((state) => [
        ...state,
        {
          id: targetedReferral.associate,
          name: targetedReferral.associate_name,
        },
      ]);
    }

    setReferredToList((state) =>
      state.filter(
        (referral) =>
          referral.associate !== associateId || referral.service !== serviceId
      )
    );
  };

  // rendering create edit referral modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText={`${title} referral`} />

      <Formik
        initialValues={{
          service: { id: appointment.id, name: appointment.service_name },
          associate: SELECT_FIELD_DEFAULT_VALUE,
          reason: '',
        }}
        validationSchema={createEditReferralSchema}
        onSubmit={createEditReferralHandler}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaFormSelect
              name='service'
              label='Select Service'
              options={
                serviceList.length
                  ? serviceList.map((service) => ({
                      id: service.id,
                      name: service.name,
                    }))
                  : []
              }
              defaultValue={props.values.service}
              setValueOnChange={(service) => setSelectedService(service)}
              optionDescription={
                serviceList.length
                  ? serviceList.map((service) =>
                      service.is_most_popular ? (
                        <FaFire
                          title='Popular Right Now'
                          className='text-base-orange'
                        />
                      ) : (
                        <></>
                      )
                    )
                  : undefined
              }
              showSearchInput
              isRequired
            />

            {referredToList.length ? (
              <ReferralModalTable
                referredToList={referredToList}
                onClickRemove={removeReferralFromList}
              />
            ) : null}

            <AcademiaFormSelect
              name='associate'
              label='Select Associate'
              options={
                associateList.length
                  ? associateList
                      .sort((a, b) => a.id - b.id)
                      .map((associate) => ({
                        id: associate.id,
                        name: associate.name,
                      }))
                  : []
              }
              defaultValue={SELECT_FIELD_DEFAULT_VALUE}
              setValueOnChange={addReferralToList}
              resetSelectField={resetField}
              setResetSelectField={() => setResetField(true)}
              showSearchInput
              isRequired
            />

            <AcademiaTextAreaField
              name='reason'
              label='Reason of Referral'
              placeholder='Enter the reason of referral here...'
              isRequired
            />

            <AcademiaButton
              // title={
              //   !selectedAssociates.length
              //     ? 'Please, select at least one associate first!'
              //     : undefined
              // }
              type='submit'
              variant='premium'
              icon={<TbSquareRoundedPlusFilled className='size-6' />}
              isLoading={props.isSubmitting}
              // isDisabled={!selectedAssociates.length}
              fullWidth
            >
              Create Referral
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditReferralModal;
